import { useCallback, useEffect, useState } from "react";
import { isAuthenticated } from "../apicalls/auth";
import {
  getAdminDemoAssets,
  createAdminDemoAsset,
  updateAdminDemoAsset,
  deleteAdminDemoAsset,
  getAdminRacketModels,
  createAdminRacketModel,
} from "../apicalls/adminDemos";
import { getRestringingVendors } from "../apicalls/restringingVendors";
import "./style/AdminDemos.css";

const ASSET_STATUS_OPTIONS = ["available", "reserved", "out", "inspection", "service", "retired"];
const OWNERSHIP_OPTIONS = ["shop", "rep", "consignment"];

function statusLabel(s) {
  if (!s) return "";
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function centsFromDollars(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return null;
  return Math.round(amount * 100);
}

function slugPart(value) {
  return String(value || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

function shortDate(value) {
  if (!value) return null;
  return String(value).slice(0, 10);
}

export function suggestedDemoAssetTag({ brand, name, grip_size }) {
  const grip = String(grip_size || "").replace(/\s+/g, "").replace("/", "");
  return ["DEMO", slugPart(brand), slugPart(name), slugPart(grip)].filter(Boolean).join("-");
}

export function demoAssetFormPayload(form) {
  return {
    ...form,
    vendor_id: Number(form.vendor_id),
    racket_model_id: Number(form.racket_model_id),
    msrp_cents: centsFromDollars(form.msrp),
  };
}

export function formatAssetDeleteBlockers(error) {
  if (!error || !Array.isArray(error.reasons) || error.reasons.length === 0) {
    return [error?.detail || error?.message || "Delete failed."];
  }

  return [
    error.detail === "asset_in_use"
      ? "Asset is in use and cannot be deleted."
      : error.detail || "Delete failed.",
    ...error.reasons.map((reason) => {
      const parts = [];
      if (reason.client_name) parts.push(reason.client_name);
      if (reason.demo_status) parts.push(statusLabel(reason.demo_status));
      if (reason.demo_payment_status) parts.push(`payment ${statusLabel(reason.demo_payment_status)}`);
      if (reason.due_on) parts.push(`due ${shortDate(reason.due_on)}`);
      parts.push(reason.returned_at ? `returned ${shortDate(reason.returned_at)}` : "not returned");
      const detail = `Demo #${reason.demo_id || "-"}, line #${reason.line_id || "-"}: ${parts.join(", ")}.`;
      return reason.message ? `${detail} ${reason.message}` : detail;
    }),
  ];
}

export function AdminDemoAssets() {
  const token = isAuthenticated()?.access_token;
  const [assets, setAssets] = useState([]);
  const [models, setModels] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteError, setDeleteError] = useState([]);
  const [filters, setFilters] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [editAsset, setEditAsset] = useState(null);
  const [form, setForm] = useState({
    vendor_id: "",
    asset_tag: "",
    racket_model_id: "",
    serial_number: "",
    grip_size: "",
    string_description: "",
    condition: "",
    ownership: "shop",
    status: "available",
    msrp: "",
    new_model_brand: "",
    new_model_name: "",
  });

  const fetchAssets = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getAdminDemoAssets(token, filters);
      if (data && Array.isArray(data.assets)) {
        setAssets(data.assets);
      } else {
        setError("Failed to load assets");
      }
    } catch (err) {
      setError(err.message || "Failed to load assets");
    }
    setLoading(false);
  }, [filters, token]);

  const fetchModels = useCallback(async () => {
    if (!token) return;
    try {
      const data = await getAdminRacketModels(token);
      if (data && Array.isArray(data.models)) {
        setModels(data.models);
      }
    } catch (err) {
      console.error("Failed to load racket models", err);
    }
  }, [token]);

  const fetchVendors = useCallback(async () => {
    if (!token) return;
    try {
      const data = await getRestringingVendors(token);
      if (data && Array.isArray(data.vendors)) {
        setVendors(data.vendors);
        if (data.vendors.length === 1) {
          setForm((prev) => prev.vendor_id ? prev : { ...prev, vendor_id: String(data.vendors[0].id) });
        }
      }
    } catch (err) {
      console.error("Failed to load vendors", err);
    }
  }, [token]);

  useEffect(() => {
    fetchAssets();
    fetchModels();
    fetchVendors();
  }, [fetchAssets, fetchModels, fetchVendors]);

  const selectedModel = models.find((m) => String(m.id) === String(form.racket_model_id));

  const updateCreateForm = (changes) => {
    setForm((prev) => {
      const next = { ...prev, ...changes };
      const model = models.find((m) => String(m.id) === String(next.racket_model_id));
      const tagSource = model || (
        next.racket_model_id === "__new__" ? { brand: next.new_model_brand, name: next.new_model_name } : null
      );
      const suggested = tagSource ? suggestedDemoAssetTag({
        brand: tagSource.brand,
        name: tagSource.name,
        grip_size: next.grip_size,
      }) : "";
      if (!next.asset_tag && suggested) next.asset_tag = suggested;
      return next;
    });
  };

  const handleStatusFilter = (status) => {
    setFilters((prev) => ({
      ...prev,
      status: prev.status === status ? undefined : status,
    }));
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setFilters((prev) => ({
      ...prev,
      search: value || undefined,
    }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setDeleteError([]);
    try {
      let racketModelId = form.racket_model_id;
      if (racketModelId === "__new__") {
        const createdModel = await createAdminRacketModel({
          brand: form.new_model_brand,
          name: form.new_model_name,
        }, token);
        if (!createdModel || !createdModel.model) {
          alert("Create failed: racket model could not be created");
          return;
        }
        racketModelId = createdModel.model.id;
        await fetchModels();
      }
      const payload = demoAssetFormPayload({ ...form, racket_model_id: racketModelId });
      delete payload.msrp;
      delete payload.new_model_brand;
      delete payload.new_model_name;
      const result = await createAdminDemoAsset(payload, token);
      if (result && result.asset) {
        setShowForm(false);
        resetForm();
        fetchAssets();
      } else {
        alert("Create failed: " + (result.detail || "unknown"));
      }
    } catch (err) {
      alert("Create failed: " + err.message);
    }
  };

  const handleUpdate = async (assetId, e) => {
    e.preventDefault();
    setDeleteError([]);
    try {
      const fd = new FormData(e.target);
      const changes = {};
      for (const [key, value] of fd.entries()) {
        if (!value) continue;
        if (key === "msrp") changes.msrp_cents = centsFromDollars(value);
        else changes[key] = key === "racket_model_id" ? Number(value) : value;
      }
      await updateAdminDemoAsset(assetId, changes, token);
      setEditAsset(null);
      fetchAssets();
    } catch (err) {
      alert("Update failed: " + err.message);
    }
  };

  const handleDelete = async (assetId) => {
    if (!window.confirm("Delete this asset?")) return;
    setDeleteError([]);
    try {
      await deleteAdminDemoAsset(assetId, token);
      fetchAssets();
    } catch (err) {
      setDeleteError(formatAssetDeleteBlockers(err));
    }
  };

  const resetForm = () => {
    setForm({
      vendor_id: vendors.length === 1 ? String(vendors[0].id) : "",
      asset_tag: "",
      racket_model_id: "",
      serial_number: "",
      grip_size: "",
      string_description: "",
      condition: "",
      ownership: "shop",
      status: "available",
      msrp: "",
      new_model_brand: "",
      new_model_name: "",
    });
  };

  if (!token) return null;

  return (
    <main>
    <div className="admin-demos">
      <h1>Demo Assets</h1>

      <div className="admin-demos-filters">
        <div className="filter-group">
          <span>Status:</span>
          {ASSET_STATUS_OPTIONS.map((s) => (
            <button
              key={s}
              className={`filter-btn ${filters.status === s ? "active" : ""}`}
              onClick={() => handleStatusFilter(s)}
            >
              {statusLabel(s)}
            </button>
          ))}
          <button
            className={`filter-btn ${filters.status === undefined ? "active" : ""}`}
            onClick={() => setFilters((p) => ({ ...p, status: undefined }))}
          >
            All
          </button>
        </div>
        <div className="filter-group">
          <input
            type="text"
            placeholder="Search tag or model..."
            value={filters.search || ""}
            onChange={handleSearch}
          />
        </div>
        <div className="filter-group">
          <button className="btn-primary" onClick={() => setShowForm(true)}>
            + New Asset
          </button>
        </div>
      </div>

      {loading && <div className="spinner" />}
      {error && <div className="error-message">{error}</div>}
      {deleteError.length > 0 && (
        <div className="error-message asset-delete-error" role="alert">
          <strong>{deleteError[0]}</strong>
          {deleteError.length > 1 && (
            <ul>
              {deleteError.slice(1).map((line, index) => (
                <li key={index}>{line}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {!loading && !error && (
        <table className="admin-demos-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Tag</th>
              <th>Vendor</th>
              <th>Model</th>
              <th>Status</th>
              <th>Ownership</th>
              <th>Condition</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {assets.length === 0 && (
              <tr><td colSpan="8">No assets found</td></tr>
            )}
            {assets.map((a) => (
              <tr key={a.id}>
                <td>{a.id}</td>
                <td>{a.asset_tag}</td>
                <td>{a.vendor_name}</td>
                <td>{a.brand} {a.model_name}</td>
                <td>
                  <span className={`demo-badge demo-badge-${a.status}`}>
                    {statusLabel(a.status)}
                  </span>
                </td>
                <td>{a.ownership}</td>
                <td>{a.condition || "-"}</td>
                <td>
                  <button className="btn-small" onClick={() => setEditAsset({ ...a })}>
                    Edit
                  </button>
                  <button className="btn-small btn-danger" onClick={() => handleDelete(a.id)}>
                    Del
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Create form */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content modal-wide" onClick={(e) => e.stopPropagation()}>
            <h3>New Demo Asset</h3>
            <form onSubmit={handleCreate}>
              <div className="form-grid">
                <label>Vendor
                  <select name="vendor_id" value={form.vendor_id} onChange={(e) => updateCreateForm({ vendor_id: e.target.value })} required>
                    <option value="">Select vendor</option>
                    {vendors.map((vendor) => (
                      <option key={vendor.id} value={vendor.id}>{vendor.name}</option>
                    ))}
                  </select>
                </label>
                <label>Asset Tag <input name="asset_tag" value={form.asset_tag} onChange={(e) => updateCreateForm({ asset_tag: e.target.value })} required /></label>
                <label>Racket Model
                  <select name="racket_model_id" value={form.racket_model_id} onChange={(e) => updateCreateForm({ racket_model_id: e.target.value, asset_tag: "" })} required>
                    <option value="">Select model</option>
                    {models.map((m) => <option key={m.id} value={m.id}>{m.brand} {m.name}</option>)}
                    <option value="__new__">Add new model...</option>
                  </select>
                </label>
                <label>Serial # <input name="serial_number" value={form.serial_number} onChange={(e) => setForm({...form, serial_number: e.target.value})} /></label>
                {form.racket_model_id === "__new__" && (
                  <>
                    <label>Brand <input name="new_model_brand" value={form.new_model_brand} onChange={(e) => updateCreateForm({ new_model_brand: e.target.value })} required /></label>
                    <label>Model <input name="new_model_name" value={form.new_model_name} onChange={(e) => updateCreateForm({ new_model_name: e.target.value })} required /></label>
                  </>
                )}
                <label>Grip Size <input name="grip_size" value={form.grip_size} onChange={(e) => updateCreateForm({ grip_size: e.target.value, asset_tag: "" })} placeholder="4 1/4" /></label>
                <label>Strings <input name="string_description" value={form.string_description} onChange={(e) => setForm({...form, string_description: e.target.value})} /></label>
                <label>Condition <input name="condition" value={form.condition} onChange={(e) => setForm({...form, condition: e.target.value})} /></label>
                <label>Ownership
                  <select name="ownership" value={form.ownership} onChange={(e) => setForm({...form, ownership: e.target.value})}>
                    {OWNERSHIP_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </label>
                <label>Status
                  <select name="status" value={form.status} onChange={(e) => setForm({...form, status: e.target.value})}>
                    {ASSET_STATUS_OPTIONS.map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}
                  </select>
                </label>
                <label>MSRP ($) <input name="msrp" type="number" step="0.01" min="0" value={form.msrp} onChange={(e) => setForm({...form, msrp: e.target.value})} /></label>
              </div>
              {selectedModel && form.grip_size && (
                <p className="form-note">Suggested tag: {suggestedDemoAssetTag({ brand: selectedModel.brand, name: selectedModel.name, grip_size: form.grip_size })}</p>
              )}
              <div className="modal-actions">
                <button type="submit">Create</button>
                <button type="button" onClick={() => setShowForm(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit form */}
      {editAsset && (
        <div className="modal-overlay" onClick={() => setEditAsset(null)}>
          <div className="modal-content modal-wide" onClick={(e) => e.stopPropagation()}>
            <h3>Edit Asset #{editAsset.id}</h3>
            <form onSubmit={(e) => handleUpdate(editAsset.id, e)}>
              <div className="form-grid">
                <label>Asset Tag <input name="asset_tag" defaultValue={editAsset.asset_tag} required /></label>
                <label>Racket Model
                  <select name="racket_model_id" defaultValue={editAsset.racket_model_id}>
                    {models.map((m) => <option key={m.id} value={m.id}>{m.brand} {m.name}</option>)}
                  </select>
                </label>
                <label>Serial # <input name="serial_number" defaultValue={editAsset.serial_number || ""} /></label>
                <label>Grip Size <input name="grip_size" defaultValue={editAsset.grip_size || ""} /></label>
                <label>Strings <input name="string_description" defaultValue={editAsset.string_description || ""} /></label>
                <label>Condition <input name="condition" defaultValue={editAsset.condition || ""} /></label>
                <label>Ownership
                  <select name="ownership" defaultValue={editAsset.ownership}>
                    {OWNERSHIP_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </label>
                <label>Status
                  <select name="status" defaultValue={editAsset.status}>
                    {ASSET_STATUS_OPTIONS.map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}
                  </select>
                </label>
                <label>MSRP ($) <input name="msrp" type="number" step="0.01" min="0" defaultValue={editAsset.msrp_cents ? (editAsset.msrp_cents / 100).toFixed(2) : ""} /></label>
              </div>
              <div className="modal-actions">
                <button type="submit">Save</button>
                <button type="button" onClick={() => setEditAsset(null)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
    </main>
  );
}
